import * as React from 'react';
import { FC, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, DialogContent, DialogTitle } from '@mui/material';
import { ShareOptions } from '../../models';
import { useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import { SwitchField, AutocompleteTextField } from '../Forms/fields';
import { useServicesSettings } from '../../store/settings/hooks';
import { getEntitiesDataItems } from '../../store/entities/selectors';
import { loadEntities } from '../../store/entities/actions';
import { UserEntity } from '../../store/entities/state';
import { getUsersAsync } from '../../ajax/requests';
import AppButton from '../AppButton';
import styled from 'styled-components';

interface Props {
    open: boolean;
    currentShare: ShareOptions | undefined;
    onClose: () => void;
    onSave: (share: ShareOptions) => Promise<void>;
}

const Actions = styled.div`
    display: flex;
    justify-content: flex-end;
    gap: var(--spacer-S);
    margin-top: var(--spacer-S);
`;

const Section = styled.div`
    margin-bottom: var(--spacer-S);
`;


const ShareOptionsModal: FC<Props> = ({ open, currentShare, onClose, onSave }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const servicesSettings = useServicesSettings();
    const users = useSelector(getEntitiesDataItems<UserEntity>('users'));

    const [shareWithClient, setShareWithClient] = useState<boolean>(currentShare?.shareWithClient ?? false);
    // The backend's WhenWritingNull serializer strips null fields, so "all professionals" (null in DB)
    // arrives as absent (undefined). When currentShare exists, absent = null in DB = all professionals.
    const [shareWithAllProfessionals, setShareWithAllProfessionals] = useState<boolean>(
        currentShare !== undefined && !Array.isArray(currentShare.shareWithProfessionalsWithService)
    );
    const [shareWithProfessionals, setShareWithProfessionals] = useState<string[]>(
        Array.isArray(currentShare?.shareWithProfessionalsWithService) ? currentShare!.shareWithProfessionalsWithService : []
    );
    const [shareWithAdministratives, setShareWithAdministratives] = useState<boolean>(currentShare?.shareWithAdministratives ?? false);
    const [sharedWithUserIds, setSharedWithUserIds] = useState<string[]>(currentShare?.sharedWithUserIds ?? []);
    const [saving, setSaving] = useState(false);

    React.useEffect(() => {
        if (open) {
            dispatch(loadEntities('users', () => getUsersAsync(), 'NewResult'));
            setShareWithClient(currentShare?.shareWithClient ?? false);
            setShareWithAllProfessionals(currentShare !== undefined && !Array.isArray(currentShare.shareWithProfessionalsWithService));
            setShareWithProfessionals(
                Array.isArray(currentShare?.shareWithProfessionalsWithService) ? currentShare!.shareWithProfessionalsWithService : []
            );
            setShareWithAdministratives(currentShare?.shareWithAdministratives ?? false);
            setSharedWithUserIds(currentShare?.sharedWithUserIds ?? []);
        }
    }, [open, currentShare]);

    const handleSave = async () => {
        setSaving(true);
        try {
            await onSave({
                shareWithClient: shareWithClient || undefined,
                shareWithProfessionalsWithService: shareWithAllProfessionals
                    ? null
                    : shareWithProfessionals,
                shareWithAdministratives: shareWithAdministratives || undefined,
                sharedWithUserIds: sharedWithUserIds.length > 0 ? sharedWithUserIds : undefined,
            });
            onClose();
        } finally {
            setSaving(false);
        }
    };

    const services = Object.entries(servicesSettings ?? {});

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{t(TK.shareOptions)}</DialogTitle>
            <DialogContent>
                <Section>
                    <SwitchField
                        label={t(TK.shareWithClient)}
                        helpText={t(TK.shareWithClientHelp)}
                        value={shareWithClient}
                        onChange={setShareWithClient}
                    />
                </Section>
                <Section>
                    <SwitchField
                        label={t(TK.shareWithAdministratives)}
                        helpText={t(TK.shareWithAdministrativesHelp)}
                        value={shareWithAdministratives}
                        onChange={setShareWithAdministratives}
                    />
                </Section>
                <Section>
                    <SwitchField
                        label={t(TK.shareWithAllProfessionals)}
                        helpText={t(TK.shareWithAllProfessionalsHelp)}
                        value={shareWithAllProfessionals}
                        onChange={setShareWithAllProfessionals}
                    />
                </Section>
                {services.length > 0 && !shareWithAllProfessionals && (
                    <Section>
                        <AutocompleteTextField
                            label={t(TK.shareWithProfessionalsWithService)}
                            multiSelect
                            options={services.map(([id]) => id)}
                            optionLabelResolver={id => servicesSettings?.[id]?.name ?? id}
                            values={shareWithProfessionals}
                            onValuesChanged={setShareWithProfessionals}
                        />
                    </Section>
                )}
                <Section>
                    <AutocompleteTextField
                        label={t(TK.shareWithSpecificUsers)}
                        helpText={t(TK.shareWithSpecificUsersHelp)}
                        multiSelect
                        options={users.map(u => u.id)}
                        optionLabelResolver={id => {
                            const u = users.find(u => u.id === id);
                            return u?.name ?? u?.username ?? id;
                        }}
                        values={sharedWithUserIds}
                        onValuesChanged={setSharedWithUserIds}
                    />
                </Section>
                <Actions>
                    <AppButton color="secondary" onClick={onClose} disabled={saving}>
                        {t(TK.cancel)}
                    </AppButton>
                    <AppButton color="success" onClick={handleSave} disabled={saving}>
                        {t(TK.save)}
                    </AppButton>
                </Actions>
            </DialogContent>
        </Dialog>
    );
};

export default ShareOptionsModal;
